import scroll from '../../helpers/scroll';
import tabTrap from '../../helpers/tabTrap';
import triggerExploreScroll from '../../helpers/triggerExploreScroll';
import triggerEditorialScroll from '../../helpers/triggerEditorialScroll';
import {gsap} from '../../vendors/gsap';
import chapters from '../chapters';
import {collapseAccordions, onAccordionClick} from './accordion';
import './menu.css';

/**
 * Moves the menu indicator to a link
 *
 * @param {HTMLLinkElement} link - pressed link
 * @returns {void}
 */
export const moveMenuIndicator = link => {
    if (link) {
        const action = link.getAttribute('data-action');
        const shouldMove = action === 'explore' || action === 'timeline' || action === 'chapters' || action === 'book';

        if (shouldMove) {
            let moveToEl = link;
            const $appNavBar = document.querySelector('.app-navbar');

            // Exception for the Chapters menu. If it was expanded, the indicator should go back to the active menu item
            if (action === 'chapters') {
                const wasExpanded = link.getAttribute('aria-expanded');

                if (wasExpanded === 'true') {
                    moveToEl = $appNavBar.querySelector('.active-menu') || link;
                }
            } else {
                // Only move to elements that are actually in the navbar.
                // This, as this might be triggered from somewhere else, e.g. the book page in the menu
                moveToEl = $appNavBar.querySelector(`[data-action="${action}"]`);
            }

            if (moveToEl) {
                gsap.to('.indicator-bar', {
                    duration: 0.5,
                    delay: 1,
                    ease: 'power3.in.out',
                    x: moveToEl.getBoundingClientRect().x - $appNavBar.offsetLeft,
                    width: moveToEl.offsetWidth
                });
            }
        }
    }
};

/**
 * Hides the menu indicator, pass true bool to reverse
 *
 * @param {boolean} hide - true to hide, false to reveal
 * @returns {void}
 */
export const hideMenuIndicator = (hide = true) => {
    if (hide) {
        gsap.to('.indicator-bar', {
            duration: 0.5,
            ease: 'power3.in.out',
            opacity: 0
        });
    } else {
        gsap.to('.indicator-bar', {
            duration: 0.5,
            ease: 'power3.in.out',
            opacity: 1
        });
    }
};

/**
 * Toggle menu size on mobile
 *
 * @param {("tall"|"short")} [state] - Force state
 * @returns {void}
 */
export const setMobileMenuHeight = (state = 'default') => {
    const menu = document.querySelector('.app-menu');

    switch (state) {
        case 'tall':
            menu.classList.replace('short-menu', 'tall-menu');
            break;
        case 'short':
            menu.classList.replace('tall-menu', 'short-menu');
            break;
        case 'default':
            if (menu.classList.contains('tall-menu')) {
                menu.classList.replace('short-menu', 'tall-menu');
            } else {
                menu.classList.replace('tall-menu', 'short-menu');
            }
            break;
    }
};

/**
 * Opens the menu and adds motion
 *
 * @returns {void}
 */
const menu = () => {
    const $backdrop = document.querySelector('[data-action=app-submenu-close]');
    const $hamburger = document.querySelector('[data-action=app-submenu-hamburger]');
    const $indicator = document.querySelector('.indicator-bar');
    const $appNavBar = document.querySelector('.app-navbar');
    const $menuContainer = document.querySelector('.app-submenu');
    const tabs =
        'a:not(.app-submenu a):not(.UsableNetAssistive), button:not(.hamburger), .swiper-button-next, .swiper-button-prev, .skip-link,.logo,.app-navbar-list,.app-content,.app-chapters-menu,.app-navbar-list,.js-onboarding-wizard';
    const $mainContainer = document.querySelector('.app-container');
    const mm = gsap.matchMedia();
    const tl = gsap.timeline({
        paused: true,
        defaults: {
            force3D: true
        },
        /**
         * Handles actions to be performed when the timeline starts its animation.
         */
        onStart: () => {
            chapters.close();
            gsap.set('.app-submenu', {autoAlpha: 1});
            $menuContainer.setAttribute('aria-modal', true);
            $hamburger.classList.add('active');
            $hamburger.setAttribute('aria-expanded', true);
            $indicator.style.width = '0';
            scroll.disable();
            triggerExploreScroll(true);
            triggerEditorialScroll(true);
            tabTrap.enable(tabs);
        },
        /**
         * Handles actions to be performed when the timeline completes its animation.
         */
        onComplete: () => {
            if ($hamburger.classList.contains('active')) {
                $hamburger.classList.remove('pointer-events-none');
                if ($mainContainer !== null) {
                    $mainContainer.classList.add('m-without_header');
                }
            }
        },
        /**
         * Handles actions to be performed when the timeline reverses its animation.
         */
        onReverseComplete: () => {
            collapseAccordions();
            gsap.set('.app-submenu', {autoAlpha: 0});
            $menuContainer.setAttribute('aria-modal', false);
            $hamburger.classList.remove('active');
            $hamburger.setAttribute('aria-expanded', false);
            scroll.enable();
            triggerExploreScroll(false);
            triggerEditorialScroll(false);
            tabTrap.disable(tabs);
            document.querySelector('.app-chapters-menu .backdrop').setAttribute('tabindex', '-1');
            if ($mainContainer !== null) {
                $mainContainer.classList.remove('m-without_header');
            }
        }
    });

    mm.add('(max-width: 1023px)', () => {
        if (tl) tl.progress(0).kill();

        tl.to(
            '.app-submenu .primary a, .app-submenu .accordion-container',
            {
                y: 0,
                opacity: 1,
                duration: 0.7,
                ease: 'power1.inOut',
                stagger: 0.07
            },
            0
        )
            .to(
                '.indicator-bar',
                {
                    opacity: 0,
                    duration: 0.7,
                    ease: 'power1.inOut'
                },
                0
            )
            .to(
                '.app-submenu',
                {
                    backdropFilter: 'blur(20px)',
                    duration: 0.6,
                    ease: 'power1.inOut'
                },
                0.1
            )
            .to(
                '.app-submenu .panel .bg',
                {
                    opacity: 1,
                    duration: 0.6,
                    ease: 'power1.inOut'
                },
                0.1
            )
            .to(
                '.app-navbar-list',
                {
                    opacity: 0,
                    duration: 0.6,
                    ease: 'power1.inOut'
                },
                0.2
            )
            .to('.app-chapters-menu', {autoAlpha: 0, duration: 0.6}, '<');
    });

    mm.add('(min-width: 1024px)', () => {
        if (tl) tl.progress(0).kill();

        tl.to(
            '.app-submenu .backdrop',
            {
                opacity: 1,
                duration: 0.6,
                ease: 'power1.inOut'
            },
            0
        )
            .to(
                '.header .bg',
                {
                    y: '-65px',
                    duration: 0.6,
                    ease: 'power4.in'
                },
                0.1
            )
            .to(
                '.app-navbar-list',
                {
                    y: '-65px',
                    duration: 0.6,
                    ease: 'power4.in'
                },
                0.1
            )
            .to(
                '.app-submenu .panel .list',
                {
                    x: 0,
                    opacity: 1,
                    duration: 0.6,
                    ease: 'power3.inOut',
                    stagger: 0.1
                },
                0.2
            )
            .to(
                '.app-submenu .primary a, .app-submenu .accordion-container',
                {
                    x: 0,
                    opacity: 1,
                    duration: 0.4,
                    ease: 'power3.inOut',
                    stagger: 0.1
                },
                0.2
            )
            .to(
                '.app-submenu .panel .left-border',
                {
                    y: 0,
                    duration: 0.4,
                    ease: 'sine.out'
                },
                0.3
            )
            .to(
                '.app-submenu .panel .bg',
                {
                    opacity: 1,
                    duration: 0.6,
                    ease: 'power1.inOut'
                },
                0.4
            )
            .to('.app-chapters-menu', {autoAlpha: 0, duration: 0.6}, '<');
    });

    /**
     * Reset the indicator position
     */
    const resetIndicatorPosition = () => {
        const link = document.querySelector('.app-navbar-list li a.active-menu');

        hideMenuIndicator(false);

        if (link) {
            gsap.to($indicator, {
                duration: 0.4,
                x: link.parentNode.getBoundingClientRect().x - $appNavBar.offsetLeft,
                width: link.parentNode.offsetWidth
            });
        }
    };

    /**
     *
     */
    const onMenuItemClick = () => {
        // move $indicator above the active menu
        const links = document.querySelectorAll('.app-navbar-list li a');

        if (links.length) {
            chapters.close();
            resetIndicatorPosition();
        }
    };

    /**
     * Handles the click event for the hamburger menu.
     *
     * When the hamburger menu is clicked:
     * - Toggles the 'active' class on the hamburger element.
     * - Updates the 'aria-expanded' attribute to reflect the new state.
     */
    const onHamburgerClick = () => {
        $hamburger.addEventListener('click', () => {
            if ($hamburger.classList.contains('pointer-events-none')) return;

            if (!$hamburger.classList.contains('active')) {
                $hamburger.classList.add('pointer-events-none');
                hideMenuIndicator();
                tl.play();
            } else {
                tl.reverse();
                resetIndicatorPosition();
            }
        });
    };

    /**
     * Handles the click event for the backdrop element.
     *
     * When the backdrop is clicked:
     * - Removes the 'active' class from the hamburger element.
     * - Sets the 'aria-expanded' attribute to 'false'.
     * - Plays the timeline animation managed by the `tl` variable.
     */
    const onBackdropClick = () => {
        $backdrop.addEventListener('click', () => {
            tl.reverse();
            resetIndicatorPosition();
        });
    };

    /**
     * Close the menu via an event
     */
    const handleClose = () => {
        tl.reverse();
        resetIndicatorPosition();
    };

    /**
     * Moves the menu indicator to the correct position when the chapters menu is toggled from within
     *
     */
    const handleToggleChapters = () => {
        // Toggle aria-expanded of the chapters menu button
        const chaptersBtn = document.querySelector('[data-action=chapters]');
        if (chaptersBtn) {
            moveMenuIndicator(chaptersBtn);
        }
    };

    /**
     * Initial data setup and event listeners
     */
    const init = () => {
        gsap.set('.app-submenu', {autoAlpha: 0});

        onBackdropClick();
        onHamburgerClick();
        onAccordionClick();
        onMenuItemClick();

        window.addEventListener('250.close.menu', handleClose);
        window.addEventListener('250.chapters.toggle', handleToggleChapters);
    };

    return {
        init
    };
};

export default menu();
